// Audit Evidence (platform compliance) — read-only proof of what the data-governance
// framework retained, anonymized, skipped, or blocked. This page NEVER fabricates
// entries: getAuditEvidence() is a STUB today and returns [], so we render an
// informative empty state (not an error toast). Compliance actions surface here once
// compliance-service records them.
import { fmtDate, fmtDateTime } from '../../lib/format';
import { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Table,
  Tag,
  Button,
  Space,
  Select,
  DatePicker,
  Input,
  Tooltip,
  Empty,
  Drawer,
  Descriptions,
  Row,
  Col,
  Statistic,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  DownloadOutlined,
  FilePdfOutlined,
  ReloadOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import { useAppSelector } from '@/app/hooks';
import { selectHasPermission } from '@/features/auth/authSlice';
import { DryRunBanner, ComplianceStatusBadge } from '@/components/compliance';
import { getAuditEvidence, type AuditEvidence } from '@/services/dataGovernanceService';

const { RangePicker } = DatePicker;

const ACTION_OPTIONS: AuditEvidence['action'][] = [
  'RETAINED',
  'ANONYMIZED',
  'DELETED',
  'SKIPPED',
  'BLOCKED',
  'DRY_RUN_EVALUATED',
  'LEGAL_HOLD_CREATED',
  'LEGAL_HOLD_RELEASED',
];

/** Show only the leading segment of an id so the table stays readable. */
const shortId = (v?: string | null): string => {
  if (!v) return '—';
  return v.length > 10 ? `${v.slice(0, 8)}…` : v;
};

const csvCell = (v: unknown): string => {
  const s = v === null || v === undefined ? '' : String(v);
  return `"${s.replace(/"/g, '""')}"`;
};

const AuditEvidencePage = () => {
  const canView = useAppSelector(selectHasPermission('compliance:audit_evidence:view'));
  const canExport = useAppSelector(selectHasPermission('compliance:audit_evidence:export'));

  const [rows, setRows] = useState<AuditEvidence[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<AuditEvidence | null>(null);

  // Filters
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState<string | undefined>();
  const [serviceFilter, setServiceFilter] = useState<string | undefined>();
  const [range, setRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getAuditEvidence();
      setRows(Array.isArray(data) ? data : []);
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not load audit evidence.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canView) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);

  // Distinct services present in the data (for the Select).
  const serviceOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.service_name).filter(Boolean))).sort(),
    [rows],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const from = range?.[0]?.startOf('day').valueOf();
    const to = range?.[1]?.endOf('day').valueOf();
    return rows.filter((r) => {
      if (actionFilter && r.action !== actionFilter) return false;
      if (serviceFilter && r.service_name !== serviceFilter) return false;
      if (from || to) {
        const t = new Date(r.performed_at).valueOf();
        if (from && t < from) return false;
        if (to && t > to) return false;
      }
      if (q) {
        const hay = [
          r.tenant_id,
          r.employee_id,
          r.subject_id,
          r.subject_type,
          r.record_reference,
          r.record_type,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, search, actionFilter, serviceFilter, range]);

  const counts = useMemo(
    () => ({
      retained: filtered.filter((r) => r.action === 'RETAINED').length,
      anonymized: filtered.filter((r) => r.action === 'ANONYMIZED').length,
      blocked: filtered.filter((r) => r.action === 'BLOCKED').length,
      dryRun: filtered.filter((r) => r.action === 'DRY_RUN_EVALUATED').length,
    }),
    [filtered],
  );

  const exportCsv = () => {
    if (!filtered.length) return;
    const headers = [
      'performed_at',
      'action',
      'service_name',
      'record_type',
      'record_reference',
      'subject_type',
      'subject_id',
      'employee_id',
      'tenant_id',
      'request_id',
      'reason',
      'policy_id',
      'legal_hold_id',
      'performed_by',
    ];
    const lines = [
      headers.join(','),
      ...filtered.map((r) =>
        [
          r.performed_at,
          r.action,
          r.service_name,
          r.record_type,
          r.record_reference,
          r.subject_type,
          r.subject_id,
          r.employee_id,
          r.tenant_id,
          r.request_id,
          r.reason,
          r.policy_id,
          r.legal_hold_id,
          r.performed_by,
        ]
          .map(csvCell)
          .join(','),
      ),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-evidence-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const columns: ColumnsType<AuditEvidence> = [
    {
      title: 'When',
      dataIndex: 'performed_at',
      key: 'performed_at',
      render: (v: string) => (v ? fmtDateTime(v) : '—'),
      sorter: (a, b) => new Date(a.performed_at).valueOf() - new Date(b.performed_at).valueOf(),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      render: (v: AuditEvidence['action']) => (
        <ComplianceStatusBadge kind="evidence_action" value={v} />
      ),
    },
    {
      title: 'Service',
      dataIndex: 'service_name',
      key: 'service_name',
      render: (v: string) => (v ? <Tag>{v}</Tag> : '—'),
    },
    { title: 'Record type', dataIndex: 'record_type', key: 'record_type' },
    {
      title: 'Record ref',
      dataIndex: 'record_reference',
      key: 'record_reference',
      render: (v?: string | null) => v || '—',
    },
    {
      title: 'Subject',
      key: 'subject',
      render: (_, r) => (
        <span>
          <Tag>{r.subject_type}</Tag>
          {shortId(r.subject_id)}
        </span>
      ),
    },
    {
      title: 'Employee',
      dataIndex: 'employee_id',
      key: 'employee_id',
      render: (v?: string | null) => shortId(v),
    },
    {
      title: 'Request',
      dataIndex: 'request_id',
      key: 'request_id',
      render: (v?: string | null) => shortId(v),
    },
    {
      title: 'Performed by',
      dataIndex: 'performed_by',
      key: 'performed_by',
      render: (v?: string | null) => v || 'system',
    },
    {
      title: '',
      key: 'actions',
      width: 96,
      render: (_, r) => (
        <Button size="small" icon={<EyeOutlined />} onClick={() => setSelected(r)}>
          Details
        </Button>
      ),
    },
  ];

  if (!canView) {
    return (
      <Card>
        <Empty description="You do not have permission to view audit evidence." />
      </Card>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <h2 style={{ marginBottom: 4 }}>Audit Evidence</h2>
        <p style={{ margin: 0, color: 'rgba(0,0,0,0.45)' }}>
          Prove what was retained, anonymized, skipped, or blocked.
        </p>
      </div>

      <DryRunBanner />

      <Row gutter={16}>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="Retained" value={counts.retained} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="Anonymized" value={counts.anonymized} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="Blocked" value={counts.blocked} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="Dry-run evaluated" value={counts.dryRun} />
          </Card>
        </Col>
      </Row>

      <Card>
        <Space style={{ marginBottom: 16, flexWrap: 'wrap' }} size="middle">
          <Tooltip title={canExport ? undefined : 'You do not have export permission.'}>
            <Button
              icon={<DownloadOutlined />}
              onClick={exportCsv}
              disabled={!canExport || filtered.length === 0}
            >
              Export CSV
            </Button>
          </Tooltip>
          <Tooltip title="PDF export not available in this release">
            <Button icon={<FilePdfOutlined />} disabled>
              Download PDF
            </Button>
          </Tooltip>
          <Button icon={<ReloadOutlined />} onClick={load}>
            Refresh
          </Button>
        </Space>

        <Space style={{ marginBottom: 16, flexWrap: 'wrap' }} size="middle">
          <Input.Search
            allowClear
            placeholder="Search tenant / employee / subject"
            style={{ width: 260 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Select
            allowClear
            placeholder="Action"
            style={{ width: 200 }}
            value={actionFilter}
            onChange={setActionFilter}
            options={ACTION_OPTIONS.map((a) => ({ value: a, label: a }))}
          />
          <Select
            allowClear
            placeholder="Service"
            style={{ width: 200 }}
            value={serviceFilter}
            onChange={setServiceFilter}
            options={serviceOptions.map((s) => ({ value: s, label: s }))}
          />
          <RangePicker value={range as any} onChange={(v) => setRange(v as any)} />
        </Space>

        <Table<AuditEvidence>
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={filtered}
          size="middle"
          scroll={{ x: 'max-content' }}
          locale={{
            emptyText: (
              <Empty
                description="No audit evidence yet. Compliance actions (anonymization, legal holds, dry-run evaluations) will appear here once recorded."
              />
            ),
          }}
        />
      </Card>

      <Drawer
        title="Audit Evidence Detail"
        width={560}
        open={!!selected}
        onClose={() => setSelected(null)}
      >
        {selected && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Action">
              <ComplianceStatusBadge kind="evidence_action" value={selected.action} />
            </Descriptions.Item>
            <Descriptions.Item label="When">
              {selected.performed_at ? fmtDateTime(selected.performed_at) : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Performed by">
              {selected.performed_by || 'system'}
            </Descriptions.Item>
            <Descriptions.Item label="Service">{selected.service_name || '—'}</Descriptions.Item>
            <Descriptions.Item label="Record type">{selected.record_type || '—'}</Descriptions.Item>
            <Descriptions.Item label="Record reference">
              {selected.record_reference || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Subject">
              <Tag>{selected.subject_type}</Tag> {selected.subject_id}
            </Descriptions.Item>
            <Descriptions.Item label="Employee">{selected.employee_id || '—'}</Descriptions.Item>
            <Descriptions.Item label="Tenant">{selected.tenant_id || '—'}</Descriptions.Item>
            <Descriptions.Item label="Request">{selected.request_id || '—'}</Descriptions.Item>
            <Descriptions.Item label="Reason">{selected.reason || '—'}</Descriptions.Item>
            <Descriptions.Item label="Policy">{selected.policy_id || '—'}</Descriptions.Item>
            <Descriptions.Item label="Legal hold">{selected.legal_hold_id || '—'}</Descriptions.Item>
            <Descriptions.Item label="Metadata">
              {selected.metadata ? (
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {JSON.stringify(selected.metadata, null, 2)}
                </pre>
              ) : (
                '—'
              )}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Drawer>
    </div>
  );
};

export default AuditEvidencePage;
